/**
 * P - Instruction Dialog — mirror Access popup `frmMatchingPSMainSubFormStockP`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubFormStockP*.txt
 *  + spec frontend/plan/frmMatchingPSMainSubFormStockP_AI_Web_Conversion_Spec.md).
 *
 * Layout 4 vùng (spec §2.3):
 *   ┌─ Header: Match Date | Note | [Save Plan] ─────────────────────────────┐
 *   ├─ Purchase filter+grid (left) ──── Factory filter+stock grid (right) ──┤
 *   ├─ Helper (usage) ──────────────────────────────────────────────────────┤
 *   └─ Detail grid (P-Stock instructions) + [Delete Plan] toolbar ──────────┘
 *
 * Logic mirror VBA `btnSavePlan_Click` (spec §8):
 *   - factory_name required ("Please choose TLC Factory!").
 *   - ≥1 row ticked ("Please tick at least one record!").
 *   - matchKg < 0 reject; matchKg > pending reject; blank → fallback pending.
 *   - Insert vào public.match_p_stock; Delete = soft delete (is_status=1).
 * Backend re-validate qua POST /matching-ps/stock-p/plans.
 *
 * Performance (đồng pattern DirectPSDialog): grid lớn lag/treo nếu render
 * non-virtualized + mount sẵn 1 TextField/row. Dùng TableVirtuoso (chỉ render
 * visible rows) + KgEditCell memo + lazy-mount KG cell (chỉ row đã tick).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import ConfirmDialog from '../../../components/ConfirmDialog'
import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  type StockPFactoryOption,
  useDeleteStockPPlanMutation,
  useGetMatchCategoriesQuery,
  useGetStockPFactoryOptionsQuery,
  useGetStockPFactoryStockQuery,
  useGetStockPPlansQuery,
  useGetStockPPurchaseCandidatesQuery,
  useSaveStockPPlanMutation,
} from '../../../store/api/matchingPsApi'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  distinctColumnValues,
  filterBarBtnSx,
  FilterButton,
  FilterPopover,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  SearchFilterContent,
  useColumnFilters,
  useFilterPopover,
} from '../../../components/dataList'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortColumn,
  type TableSortState,
  toggleTableSort,
} from '../../../utils/tableSort'
import {
  useMatchingFilterPref,
  type MatchingFilterCtx,
  type StockFactoryFilterCtx,
} from './matchingFilterPrefs'
import {
  matchingFilterAutocompleteSx,
  matchingRangeDateFieldSx,
} from './matchingFilterUi'

/* ---------- formatters (đồng nhất với DirectPSDialog) -------------------- */

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function fmtDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function fmtDateTime(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

function fmtNumber(value: unknown): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

function toNumber(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/* ---------- defaults ----------------------------------------------------- */

type DateRange = { from: string; to: string }

const ACCENT_P = '#1e88e5' // Purchase side accent
const ACCENT_STOCK = '#43a047' // TLC Factory/Stock accent

function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4 = now.getMonth() >= 9
  const startYear = inQ4 ? y : y - 1
  const endYear = inQ4 ? y + 1 : y
  return { from: `${startYear}-10-01`, to: `${endYear}-09-30` }
}

/* ---------- column defs -------------------------------------------------- */

interface GridCol {
  key: string
  label: string
  width: number
  kind?: 'date' | 'datetime' | 'number' | 'text' | 'check' | 'editKg'
  align?: 'left' | 'right' | 'center'
  /** Override per-column filter kind. 'none' = bỏ icon ▽ (check/editKg). */
  filter?: ColumnFilterKind
  /** Access Format `#,##0;(#,##0);-` → giá trị 0 hiển thị "-". */
  dashZero?: boolean
}

// Pending field của Purchase candidate (p_plan_pending → camelCase).
const PENDING_KEY = 'pPlanPending'

// Purchase candidate columns — spec §3.2 (subFormP). Order/checkbox-first +
// KG(EDIT) ngay sau Pending để đồng bộ ergonomics với DirectPSDialog P side.
const P_CANDIDATE_COLS: GridCol[] = [
  { key: 'checked_row', label: 'IsMatch?', width: 36, kind: 'check', align: 'center', filter: 'none' },
  { key: 'contractNo', label: 'ContractNo', width: 80 },
  { key: 'cpName', label: 'Supplier', width: 110 },
  { key: 'pPlanPending', label: 'Pending', width: 64, kind: 'number', align: 'right' },
  { key: 'match_kg', label: 'KG(EDIT)', width: 90, kind: 'editKg', filter: 'none' },
  { key: 'latestDelivery', label: 'LatestDelivery', width: 90, kind: 'date' },
  { key: 'deliveryPlace', label: 'Place', width: 96 },
  { key: 'qualityCode', label: 'Quality', width: 50 },
  { key: 'category', label: 'Grade', width: 50 },
  { key: 'quantityKg', label: 'ContractQuant', width: 78, kind: 'number', align: 'right' },
]

// Factory/Stock quality grid — spec §3.3 (subFormStock). Source form không nằm
// trong file upload; mapping theo view backend `v_match_report_stockquality`.
const STOCK_COLS: GridCol[] = [
  { key: 'factoryCode', label: 'FactoryCode', width: 90 },
  { key: 'grade', label: 'Grade', width: 70 },
  { key: 'qualityCode', label: 'QualityCode', width: 84 },
  { key: 'stockNw', label: 'Stack NW', width: 90, kind: 'number', align: 'right', dashZero: true },
  { key: 'pToExecute', label: 'P-ToExecute', width: 92, kind: 'number', align: 'right', dashZero: true },
  { key: 'sToExecute', label: 'S-ToExecute', width: 92, kind: 'number', align: 'right', dashZero: true },
  { key: 'stockAvailable', label: 'Available Stock', width: 100, kind: 'number', align: 'right' },
  { key: 'description', label: 'QualityDescription', width: 170 },
]

// P-Stock instruction detail grid — spec §3.4 (subFormD, MatchingPSQryReportStockP).
const DETAIL_COLS: GridCol[] = [
  { key: 'matchId', label: 'ID', width: 60, kind: 'number', align: 'right' },
  { key: 'matchDate', label: 'InstructDate', width: 100, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 88, kind: 'number', align: 'right' },
  { key: 'pSide', label: 'P Contract', width: 100 },
  { key: 'pName', label: 'P Supplier', width: 130 },
  { key: 'pQuality', label: 'P Quality', width: 70 },
  { key: 'pGrade', label: 'P Grade', width: 70 },
  { key: 'factoryName', label: 'StockSide', width: 100 },
  { key: 'pQuantity', label: 'P Quantity', width: 86, kind: 'number', align: 'right' },
  { key: 'pPlanPending', label: 'P Pending', width: 86, kind: 'number', align: 'right' },
  { key: 'pDirectS', label: 'P Direct', width: 80, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 88, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 88, kind: 'number', align: 'right' },
  { key: 'pPlace', label: 'P DeliveryPlace', width: 120 },
  { key: 'npeRef', label: 'P NpeRef', width: 90 },
  { key: 'note', label: 'Note', width: 140 },
  { key: 'dateUpdated', label: 'Updated Date', width: 140, kind: 'datetime' },
  { key: 'personUpdated', label: 'Updated By', width: 130 },
]

// Shared header style — gradient + opaque bg + sticky (đồng bộ DirectPSDialog).
const HEADER_CELL_SX = {
  whiteSpace: 'nowrap' as const,
  position: 'sticky' as const,
  top: 0,
  zIndex: 5,
  backgroundColor: '#fff9f4',
  backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
  color: '#0b3633',
  fontSize: '0.6rem',
  fontWeight: 900,
  lineHeight: 1.1,
  letterSpacing: '0.01em',
  borderTop: '1px solid rgba(11, 54, 51, 0.16)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.34)',
  boxShadow: '0 2px 0 rgba(11, 54, 51, 0.08)',
  opacity: 1,
  py: 0.3,
  px: 0.3,
}

function fmtCell(row: Record<string, unknown>, col: GridCol): string {
  const v = row[col.key]
  if (col.kind === 'date') return fmtDate(v)
  if (col.kind === 'datetime') return fmtDateTime(v)
  if (col.kind === 'number') return col.dashZero && toNumber(v) === 0 ? '-' : fmtNumber(v)
  if (v == null || v === '') return ''
  return String(v)
}

/* ---------- KG(EDIT) cell — memo + local state để fix lag ----------------- */

/**
 * Mỗi keystroke trên KG TextField KHÔNG được gọi parent setState (parent
 * re-render → grid re-render hết visible rows = nhiều TextField MUI mount lại).
 * KgEditCell: giữ local state, chỉ commit lên parent khi blur; chỉ nhận digits;
 * sync external khi parent reset (vd sau Save Plan setMatchKg({})).
 */
const KgEditCell = memo(function KgEditCell({
  cn,
  pendingValue,
  externalValue,
  width,
  onCommit,
}: {
  cn: string
  pendingValue: unknown
  externalValue: string
  width: number
  onCommit: (cn: string, value: string) => void
}) {
  const [local, setLocal] = useState(externalValue)
  const [focused, setFocused] = useState(false)
  const lastExternalRef = useRef(externalValue)

  useEffect(() => {
    if (externalValue !== lastExternalRef.current) {
      setLocal(externalValue)
      lastExternalRef.current = externalValue
    }
  }, [externalValue])

  // Display formatted (1,234,567) khi blur; plain digits khi focus để cursor
  // không jump. State `local` luôn plain digits → Number(local) safe.
  const displayed = focused ? local : local ? fmtNumber(Number(local)) : ''

  // Spec §14.4 — highlight đỏ + bold khi value > plan_pending (invalid trước
  // khi Save). Mirror Access ConditionalFormat trên match_kg.
  const numericValue = local === '' ? null : Number(local)
  const pendingNum = toNumber(pendingValue)
  const isOverPending =
    numericValue != null && Number.isFinite(numericValue) && pendingNum > 0 && numericValue > pendingNum

  return (
    <TextField
      size="small"
      type="text"
      inputMode="numeric"
      placeholder={String(fmtNumber(pendingValue) || '')}
      value={displayed}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d]/g, '')
        if (v === '' || /^\d+$/.test(v)) setLocal(v)
      }}
      onBlur={() => {
        setFocused(false)
        if (local !== externalValue) {
          lastExternalRef.current = local
          onCommit(cn, local)
        }
      }}
      inputProps={{
        style: {
          fontSize: '0.62rem',
          padding: '1px 3px',
          textAlign: 'right',
          ...(isOverPending && { color: '#ED1C24', fontWeight: 700 }),
        },
      }}
      sx={{
        width: width - 8,
        '& .MuiOutlinedInput-root': { fontSize: '0.62rem' },
      }}
    />
  )
})

/* ---------- props -------------------------------------------------------- */

export interface StockPInstructionDialogProps {
  open: boolean
  onClose: () => void
  canEdit: boolean
}

export default function StockPInstructionDialog({
  open,
  onClose,
  canEdit,
}: StockPInstructionDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '95vh', maxHeight: '95vh' } }}
    >
      {/* Mount body on open → reset state mỗi lần mở. */}
      {open ? <StockPInstructionDialogBody onClose={onClose} canEdit={canEdit} /> : null}
    </Dialog>
  )
}

/* ---------- body --------------------------------------------------------- */

function StockPInstructionDialogBody({
  onClose,
  canEdit,
}: {
  onClose: () => void
  canEdit: boolean
}) {
  const snack = useSnackbar()
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), [])

  // Header inputs
  const [matchDate, setMatchDate] = useState(today)
  const [note, setNote] = useState('')

  // Purchase filter (backend query)
  const [categoryP, setCategoryP] = useState('')
  const [rangeP, setRangeP] = useState<DateRange>(fiscalDefault)

  // Factory filter (backend query)
  const [categoryF, setCategoryF] = useState('')
  const [factoryCode, setFactoryCode] = useState('')

  // Persist 2 filter context qua user_preferences (skill /user-logaction) theo
  // đúng pattern Direct P-S dialog:
  //   - stock-p        : Purchase candidate filter (grade + Latest Delivery range)
  //   - stock-factory  : TLC Factory filter (grade + factory) — DÙNG CHUNG với
  //                      S-Instruction dialog (mirror Access module match_factory).
  // Patch per-field khi user đổi control; queries gate trên `hydrated` để không
  // fetch với default trước khi prefs về. Body re-mount mỗi lần mở → hydrate lại.
  const purchaseFilterPref = useMatchingFilterPref<MatchingFilterCtx>('stock-p')
  const factoryFilterPref = useMatchingFilterPref<StockFactoryFilterCtx>('stock-factory')
  const { patch: patchPurchaseFilter } = purchaseFilterPref
  const { patch: patchFactoryFilter } = factoryFilterPref
  const hydratedRef = useRef(false)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    if (hydratedRef.current || purchaseFilterPref.isLoading || factoryFilterPref.isLoading) return
    hydratedRef.current = true
    const pv = purchaseFilterPref.value
    const fv = factoryFilterPref.value
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate-once filter từ prefs (ref-latched)
    setCategoryP(pv.category ?? '')
    setRangeP({ from: pv.dateFrom || fiscalDefault.from, to: pv.dateTo || fiscalDefault.to })
    setCategoryF(fv.category ?? '')
    setFactoryCode(fv.factoryCode ?? '')
    setHydrated(true)
  }, [purchaseFilterPref, factoryFilterPref, fiscalDefault])

  // Wrapped handlers — set local state (drive query args) + persist delta.
  const handleCategoryP = useCallback(
    (v: string) => {
      setCategoryP(v)
      void patchPurchaseFilter({ category: v })
    },
    [patchPurchaseFilter],
  )
  const handleRangeP = useCallback(
    (next: DateRange) => {
      setRangeP(next)
      void patchPurchaseFilter({ dateFrom: next.from || null, dateTo: next.to || null })
    },
    [patchPurchaseFilter],
  )
  const handleCategoryF = useCallback(
    (v: string) => {
      setCategoryF(v)
      void patchFactoryFilter({ category: v })
    },
    [patchFactoryFilter],
  )
  const handleFactoryCode = useCallback(
    (v: string) => {
      setFactoryCode(v)
      void patchFactoryFilter({ factoryCode: v })
    },
    [patchFactoryFilter],
  )

  // Selection / row inputs
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [matchKgMap, setMatchKgMap] = useState<Record<string, string>>({})
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  // Detail grid — client-side filter/sort layer (Grade/Factory phía trên là
  // backend query, lớp này lọc trong rows đã load về). Skill /table-style.
  const [searchTermD, setSearchTermD] = useState('')
  const searchPopoverD = useFilterPopover<'search'>()
  const columnFiltersD = useColumnFilters()
  const [colPopoverD, setColPopoverD] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [sortStateD, setSortStateD] = useState<TableSortState<string> | null>(null)

  /* ---------- fetches --------------------------------------------------- */

  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  const factoryOptionsQuery = useGetStockPFactoryOptionsQuery()
  const factoryOptions = useMemo(
    () => factoryOptionsQuery.data ?? [],
    [factoryOptionsQuery.data],
  )

  // KHÔNG auto-pick factory: để trống = "All" → xem toàn bộ factory (query truyền
  // factoryCode=undefined → BE bỏ filter). User chọn 1 factory khi cần instruct
  // (Save validate factory_code riêng). Bỏ auto-pick theo yêu cầu 2026-05-27.

  const candidatesQuery = useGetStockPPurchaseCandidatesQuery(
    {
      startDate: rangeP.from || undefined,
      endDate: rangeP.to || undefined,
      category: categoryP || undefined,
    },
    { skip: !hydrated },
  )
  const stockQuery = useGetStockPFactoryStockQuery(
    {
      factoryCode: factoryCode || undefined,
      category: categoryF || undefined,
    },
    { skip: !hydrated },
  )
  const plansQuery = useGetStockPPlansQuery(
    {
      factoryCode: factoryCode || undefined,
      category: categoryF || undefined,
    },
    { skip: !hydrated },
  )

  const candidates = useMemo(
    () => (candidatesQuery.data ?? []) as Record<string, unknown>[],
    [candidatesQuery.data],
  )
  const stockRows = useMemo(
    () => (stockQuery.data ?? []) as Record<string, unknown>[],
    [stockQuery.data],
  )
  const detailRows = useMemo(
    () => (plansQuery.data ?? []) as Record<string, unknown>[],
    [plansQuery.data],
  )

  // Detail: global Search + per-column ▽ filter → sort. TableVirtuoso render
  // sortedDetailRows.
  const filteredDetailRows = useMemo(
    () =>
      detailRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTermD, DETAIL_COLS)
          && rowMatchesAllColumnFilters(r, columnFiltersD.filters),
      ),
    [detailRows, searchTermD, columnFiltersD.filters],
  )
  const sortedDetailRows = useMemo(
    () =>
      sortTableRows(
        filteredDetailRows,
        sortStateD,
        DETAIL_COLS as ReadonlyArray<TableSortColumn<string>>,
      ),
    [filteredDetailRows, sortStateD],
  )

  const hasActiveDetailFilters = !!searchTermD.trim() || columnFiltersD.hasAny
  const activeDetailFilterCount =
    (searchTermD.trim() ? 1 : 0) + columnFiltersD.activeKeys.length
  const clearAllDetailFilters = () => {
    setSearchTermD('')
    columnFiltersD.clearAll()
    searchPopoverD.close()
  }

  const popoverDetailColumn = useMemo(
    () => (colPopoverD ? DETAIL_COLS.find((c) => c.key === colPopoverD.key) ?? null : null),
    [colPopoverD],
  )
  const popoverDetailKind = useMemo<ColumnFilterKind>(
    () =>
      popoverDetailColumn
        ? inferColumnFilterKind(popoverDetailColumn as never, detailRows)
        : 'text',
    [popoverDetailColumn, detailRows],
  )
  const popoverDetailOptions = useMemo(
    () =>
      popoverDetailKind === 'checkboxes' && colPopoverD
        ? distinctColumnValues(detailRows, colPopoverD.key)
        : undefined,
    [popoverDetailKind, colPopoverD, detailRows],
  )

  const toggleSortD = (key: string) => setSortStateD((prev) => toggleTableSort(prev, key))

  /* ---------- mutations -------------------------------------------------- */

  const [savePlan, { isLoading: saving }] = useSaveStockPPlanMutation()
  const [deletePlan, { isLoading: deleting }] = useDeleteStockPPlanMutation()

  /* ---------- handlers --------------------------------------------------- */

  // Stable handler — KgEditCell memo dùng reference-equality để skip re-render.
  const handleMatchKg = useCallback((cn: string, value: string) => {
    setMatchKgMap((prev) => ({ ...prev, [cn]: value }))
  }, [])

  // Tick → autofill matchKg = pending; untick → clear matchKg (tránh "rác").
  const handleToggleRow = useCallback((cn: string, pending: number) => {
    let wasChecked = false
    setChecked((prev) => {
      wasChecked = prev.has(cn)
      const next = new Set(prev)
      if (wasChecked) next.delete(cn)
      else next.add(cn)
      return next
    })
    setMatchKgMap((prev) => {
      const next = { ...prev }
      if (wasChecked) delete next[cn]
      else next[cn] = String(Math.round(pending))
      return next
    })
  }, [])

  // Toggle all: tick all → autofill matchKg cho TẤT CẢ candidate rows = pending;
  // untick all → clear sạch (1 setState thay vì N).
  const handleToggleAll = useCallback(
    (allChecked: boolean) => {
      if (allChecked) {
        setChecked(new Set())
        setMatchKgMap({})
      } else {
        const nextChecked = new Set<string>()
        const nextMk: Record<string, string> = {}
        candidates.forEach((r) => {
          const cn = String(r.contractNo ?? '')
          const pending = toNumber(r[PENDING_KEY])
          nextChecked.add(cn)
          nextMk[cn] = String(Math.round(pending))
        })
        setChecked(nextChecked)
        setMatchKgMap(nextMk)
      }
    },
    [candidates],
  )

  // Save core — validate + mutation.
  const handleSave = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (!factoryCode) {
      snack.warning('Please choose TLC Factory!')
      return
    }
    if (!matchDate) {
      snack.error('Match Date is required.')
      return
    }
    const items = candidates
      .filter((r) => checked.has(String(r.contractNo ?? '')))
      .map((r) => {
        const cn = String(r.contractNo ?? '')
        const raw = matchKgMap[cn]
        const mk = raw == null || raw === '' ? null : Number(raw)
        return {
          pSide: cn,
          planPending: toNumber(r[PENDING_KEY]),
          matchKg: Number.isFinite(mk as number) ? (mk as number) : null,
        }
      })
    if (items.length === 0) {
      snack.warning('Please tick at least one record!')
      return
    }
    // Client-side bound check — backend re-validate.
    for (const it of items) {
      const qty = it.matchKg ?? it.planPending
      if (qty < 0) {
        snack.warning('Cannot input KG(EDIT) < 0 !')
        return
      }
      if (qty > it.planPending) {
        snack.warning('Cannot input KG(EDIT) > Pending !')
        return
      }
    }
    try {
      const res = await savePlan({
        matchDate,
        note,
        factoryName: factoryCode,
        items,
      }).unwrap()
      snack.success(res.message || 'Successfully Updated')
      setChecked(new Set())
      setMatchKgMap({})
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Save failed'
      snack.error(msg)
    }
  }

  const handleDelete = () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (selectedDetailId == null) {
      snack.warning('Select a P-Stock row below before clicking Delete Plan.')
      return
    }
    setConfirmDeleteOpen(true)
  }

  const confirmDelete = async () => {
    if (selectedDetailId == null) return
    try {
      const res = await deletePlan({ matchId: selectedDetailId }).unwrap()
      snack.success(res.message || 'Successfully Deleted')
      setSelectedDetailId(null)
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Delete failed'
      snack.error(msg)
    }
  }

  const handleReloadAll = () => {
    void candidatesQuery.refetch()
    void stockQuery.refetch()
    void plansQuery.refetch()
  }

  // Centered spinner cho lần fetch đầu (isLoading chỉ true ở fetch đầu; refetch
  // dùng isFetching → giữ body visible với inline spinner).
  const isInitialLoading =
    !hydrated || candidatesQuery.isLoading || factoryOptionsQuery.isLoading || plansQuery.isLoading

  /* ---------- render ----------------------------------------------------- */

  return (
    <>
      <DialogTitle
        component="div"
        sx={{
          pl: { xs: 1.25, sm: 2 },
          pr: { xs: 0.75, sm: 1 },
          py: { xs: 0.5, sm: 1 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          minWidth: 0,
        }}
      >
        <Chip
          size="small"
          label="P"
          sx={{
            height: 20,
            bgcolor: ACCENT_P,
            color: '#fff',
            fontWeight: 700,
            '& .MuiChip-label': { px: 0.9 },
            flexShrink: 0,
          }}
        />
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          title="P - INSTRUCTION"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '0.82rem', sm: '0.95rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          P - INSTRUCTION
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload all grids">
          <span>
            <IconButton size="small" onClick={handleReloadAll} disabled={saving || deleting}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {isInitialLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 360,
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                Loading…
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {!canEdit ? (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Read-only — you don't have edit permission for this module.
              </Alert>
            ) : null}

            {/* Header: Match Date + Note + Save Plan — merged 1 row. */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 0.5 }}
            >
              <DatePicker
                label="Match Date"
                format="DD-MMM-YYYY"
                value={matchDate ? dayjs(matchDate) : null}
                onChange={(d) => setMatchDate(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
                    sx: { minWidth: 160, '& .MuiInputBase-input': { fontSize: '0.74rem', py: 0.5 } },
                  },
                }}
              />
              <TextField
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.78rem' } }}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  '& .MuiInputBase-input': { fontSize: '0.74rem', py: 0.5 },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <SaveOutlinedIcon />
                }
                onClick={() => void handleSave()}
                disabled={!canEdit || saving}
              >
                Save
              </Button>
            </Stack>

            {/* Hướng dẫn sử dụng — 1 dòng caption inline. */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 0.5, color: 'text.secondary' }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'info.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontStyle: 'italic', lineHeight: 1.3 }}>
                Choose a <b>TLC Factory</b> on the right, then tick Purchase rows to instruct.
                {' • '}
                Tick → KG(EDIT) auto-loads <b>Pending</b>; edit to allocate partial.
                {' • '}
                KG(EDIT) turns{' '}
                <Box component="span" sx={{ color: '#ED1C24', fontWeight: 700 }}>red</Box>
                {' '}if &gt; Pending (invalid).
                {' • '}
                Click a detail row below → <b>Delete Plan</b>.
              </Typography>
            </Stack>

            {/* 2-column area: Purchase candidate | Factory stock. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 0.5,
                flex: '7 1 0',
                minHeight: 280,
              }}
            >
              <PurchaseCandidatePanel
                category={categoryP}
                onCategoryChange={handleCategoryP}
                range={rangeP}
                onRangeChange={handleRangeP}
                categoryOptions={categoryOptions}
                rows={candidates}
                checked={checked}
                onToggle={handleToggleRow}
                onToggleAll={handleToggleAll}
                matchKg={matchKgMap}
                onMatchKgChange={handleMatchKg}
                isFetching={candidatesQuery.isFetching}
              />
              <FactoryStockPanel
                category={categoryF}
                onCategoryChange={handleCategoryF}
                factoryCode={factoryCode}
                onFactoryChange={handleFactoryCode}
                factoryOptions={factoryOptions}
                categoryOptions={categoryOptions}
                rows={stockRows}
                isFetching={stockQuery.isFetching}
              />
            </Box>

            <Divider />

            {/* Detail title + client filter (Search + ▽) + Delete — merged 1 row. */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 0.5 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
                P-STOCK INSTRUCTIONS
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                ({sortedDetailRows.length.toLocaleString('en-US')}
                {sortedDetailRows.length !== detailRows.length
                  ? `/${detailRows.length.toLocaleString('en-US')}`
                  : ''})
              </Typography>
              {plansQuery.isFetching ? <CircularProgress size={11} sx={{ ml: 0.5 }} /> : null}
              <FilterButton
                label="Search"
                active={!!searchTermD.trim()}
                onClick={(e) => searchPopoverD.open('search', e)}
              />
              {hasActiveDetailFilters ? (
                <Button
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={clearAllDetailFilters}
                  sx={filterBarBtnSx}
                >
                  Clear ({activeDetailFilterCount})
                </Button>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <Tooltip
                title={
                  selectedDetailId == null
                    ? 'Click a row to select, then click Delete Plan.'
                    : `Selected ID: ${selectedDetailId}`
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      deleting ? <CircularProgress size={11} sx={{ color: 'inherit' }} /> : <DeleteOutlineIcon />
                    }
                    onClick={handleDelete}
                    disabled={!canEdit || deleting || selectedDetailId == null}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.2,
                      minHeight: 26,
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      lineHeight: 1.1,
                      '& .MuiButton-startIcon': { mr: 0.4 },
                      '& .MuiButton-startIcon > *:first-of-type': { fontSize: 14 },
                    }}
                  >
                    Delete Plan
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                flex: '3 1 0',
                minHeight: 120,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {sortedDetailRows.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: 120,
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    {detailRows.length === 0
                      ? 'No active P-Stock instructions for this filter.'
                      : 'No rows match the current filters.'}
                  </Typography>
                </Box>
              ) : (
                <TableVirtuoso
                  style={{ height: '100%' }}
                  data={sortedDetailRows}
                  fixedHeaderContent={() => (
                    <TableRow>
                      {DETAIL_COLS.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.align ?? 'left'}
                          onClick={() => toggleSortD(col.key)}
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{ ...HEADER_CELL_SX, cursor: 'pointer', userSelect: 'none' }}
                        >
                          {col.label}
                          {getTableSortSuffix(sortStateD, col.key)}
                          {col.filter !== 'none' ? (
                            <ColumnFilterIcon
                              active={columnFiltersD.isActive(col.key)}
                              onClick={(position) => setColPopoverD({ key: col.key, position })}
                              ariaLabel={`Filter ${col.label}`}
                            />
                          ) : null}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                  itemContent={(_, row) => (
                    <>
                      {DETAIL_COLS.map((col) => {
                        const text = fmtCell(row, col)
                        // CF cột P Pending: p_plan_pending < 0 → đỏ + bold (acLessThan, value 0).
                        const cfRed = col.key === 'pPlanPending' && toNumber(row.pPlanPending) < 0
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align ?? 'left'}
                            style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                            sx={{
                              py: 0.15,
                              px: 0.4,
                              fontSize: '0.62rem',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              ...(cfRed && { color: '#ED1C24', fontWeight: 700 }),
                            }}
                          >
                            <Box
                              component="span"
                              title={text || '-'}
                              sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {text || '-'}
                            </Box>
                          </TableCell>
                        )
                      })}
                    </>
                  )}
                  components={{
                    Table: (props: any) => (
                      <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />
                    ),
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                    TableRow: (props: any) => {
                      const idx = Number(props['data-item-index'])
                      const row = Number.isFinite(idx) ? sortedDetailRows[idx] : null
                      const mid = row ? toNumber((row as Record<string, unknown>).matchId) : null
                      const isSelected = mid != null && selectedDetailId === mid
                      return (
                        <TableRow
                          {...props}
                          hover
                          onClick={() => setSelectedDetailId(mid || null)}
                          sx={{
                            cursor: 'pointer',
                            ...(isSelected && {
                              '& .MuiTableCell-root': {
                                bgcolor: (t: any) => `${alpha(t.palette.primary.main, 0.16)} !important`,
                                fontWeight: 700,
                              },
                            }),
                          }}
                        />
                      )
                    },
                  }}
                />
              )}
            </Box>

            <FilterPopover
              open={searchPopoverD.state !== null}
              anchorEl={searchPopoverD.state?.anchor ?? null}
              onClose={() => searchPopoverD.close()}
            >
              {searchPopoverD.isOpen('search') && (
                <SearchFilterContent
                  value={searchTermD}
                  onChange={(v) => setSearchTermD(v)}
                  placeholder="ID, contract, supplier, place, note..."
                  onClear={() => searchPopoverD.close()}
                />
              )}
            </FilterPopover>

            <ColumnFilterPopover
              open={colPopoverD !== null}
              anchorPosition={colPopoverD?.position ?? null}
              onClose={() => setColPopoverD(null)}
              label={popoverDetailColumn?.label ?? ''}
              kind={popoverDetailKind === 'none' ? 'text' : popoverDetailKind}
              value={colPopoverD ? columnFiltersD.filters[colPopoverD.key] : undefined}
              onChange={(v) => {
                if (!colPopoverD) return
                columnFiltersD.setFilter(colPopoverD.key, v)
              }}
              options={popoverDetailOptions}
            />
          </>
        )}
      </DialogContent>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete Plan"
        message={
          selectedDetailId != null
            ? `Do you want to delete plan with ID = '${selectedDetailId}'?`
            : ''
        }
        confirmLabel="Delete Plan"
        danger
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </>
  )
}

/* ---------- Purchase candidate panel (editable: checkbox + KG) ------------ */

interface PurchaseCandidatePanelProps {
  category: string
  onCategoryChange: (v: string) => void
  range: DateRange
  onRangeChange: (r: DateRange) => void
  categoryOptions: string[]
  rows: Record<string, unknown>[]
  checked: Set<string>
  onToggle: (contractNo: string, pending: number) => void
  onToggleAll: (allChecked: boolean) => void
  matchKg: Record<string, string>
  onMatchKgChange: (contractNo: string, value: string) => void
  isFetching: boolean
}

function PurchaseCandidatePanel({
  category,
  onCategoryChange,
  range,
  onRangeChange,
  categoryOptions,
  rows,
  checked,
  onToggle,
  onToggleAll,
  matchKg,
  onMatchKgChange,
  isFetching,
}: PurchaseCandidatePanelProps) {
  const columns = P_CANDIDATE_COLS
  const accent = ACCENT_P

  // Client-side Search + per-column ▽ filter + sort (tách với Grade/Date backend).
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns)
          && rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columnFilters.filters, columns],
  )
  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        columns as ReadonlyArray<TableSortColumn<string>>,
      ),
    [filteredRows, sortState, columns],
  )

  const allChecked =
    sortedRows.length > 0 && sortedRows.every((r) => checked.has(String(r.contractNo ?? '')))
  const someChecked = sortedRows.some((r) => checked.has(String(r.contractNo ?? '')))

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const toggleSort = (key: string) => setSortState((prev) => toggleTableSort(prev, key))

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderLeftWidth: 3,
        borderLeftColor: accent,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ px: 0.5, py: 0.3, bgcolor: alpha(accent, 0.06), borderBottom: 1, borderColor: 'divider' }}
      >
        <Chip
          size="small"
          label="P"
          sx={{ height: 18, bgcolor: accent, color: '#fff', fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem' } }}
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', color: 'text.secondary' }}
        >
          ({sortedRows.length.toLocaleString('en-US')}
          {sortedRows.length !== rows.length ? `/${rows.length.toLocaleString('en-US')}` : ''})
        </Typography>
        {isFetching ? <CircularProgress size={10} sx={{ ml: 0.5, color: accent }} /> : null}
        <Box sx={{ width: 8 }} />
        <Autocomplete
          size="small"
          options={categoryOptions}
          value={category || null}
          onChange={(_e, v) => onCategoryChange(v ?? '')}
          getOptionLabel={(o) => o || ''}
          isOptionEqualToValue={(a, b) => a === b}
          sx={{ flex: '1 1 0', minWidth: { xs: 72, sm: 90 } }}
          renderInput={(params) => (
            <TextField {...params} label="Grade"
              placeholder="(all)"
              InputLabelProps={{ shrink: true }}
              sx={matchingFilterAutocompleteSx} />
          )}
        />
        <DatePicker
          label="Latest Delivery Period"
          format="DD-MMM-YYYY"
          value={range.from ? dayjs(range.from) : null}
          onChange={(d) =>
            onRangeChange({ ...range, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true },
              sx: matchingRangeDateFieldSx,
              inputProps: { 'aria-label': 'Latest Delivery start date' },
            },
            openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
            openPickerIcon: { sx: { fontSize: { xs: 12, sm: 14 } } },
          }}
        />
        <ArrowRightAltIcon
          sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.disabled', flexShrink: 0, mx: { xs: 0.1, sm: 0.25 } }}
          aria-hidden="true"
        />
        <DatePicker
          format="DD-MMM-YYYY"
          value={range.to ? dayjs(range.to) : null}
          onChange={(d) =>
            onRangeChange({ ...range, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              sx: matchingRangeDateFieldSx,
              inputProps: { 'aria-label': 'Latest Delivery end date' },
            },
            openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
            openPickerIcon: { sx: { fontSize: { xs: 12, sm: 14 } } },
          }}
        />
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
        {hasActiveFilters ? (
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={clearAllFilters}
            sx={filterBarBtnSx}
          >
            Clear ({activeFilterCount})
          </Button>
        ) : null}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
        {sortedRows.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
            }}
          >
            <Typography variant="caption" color="text.disabled">
              {rows.length === 0
                ? 'No Purchase with pending > 0.'
                : 'No rows match the current filters.'}
            </Typography>
          </Box>
        ) : (
          <TableVirtuoso
            style={{ height: '100%' }}
            data={sortedRows}
            fixedHeaderContent={() => (
              <TableRow>
                {columns.map((col) => {
                  if (col.kind === 'check') {
                    return (
                      <TableCell
                        key={col.key}
                        padding="checkbox"
                        align="center"
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={HEADER_CELL_SX}
                      >
                        <Box
                          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onChange={() => onToggleAll(allChecked)}
                            sx={{ p: 0.15 }}
                            aria-label={col.label}
                          />
                        </Box>
                      </TableCell>
                    )
                  }
                  const sortable = col.kind !== 'editKg'
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      onClick={sortable ? () => toggleSort(col.key) : undefined}
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{
                        ...HEADER_CELL_SX,
                        cursor: sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      {col.label}
                      {sortable ? getTableSortSuffix(sortState, col.key) : null}
                      {col.filter !== 'none' ? (
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                      ) : null}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_, row) => {
              const cn = String(row.contractNo ?? '')
              const isChecked = checked.has(cn)
              return (
                <>
                  {columns.map((col) => {
                    if (col.kind === 'check') {
                      return (
                        <TableCell
                          key={col.key}
                          padding="checkbox"
                          align="center"
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{ py: 0.1 }}
                        >
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => onToggle(cn, toNumber(row[PENDING_KEY]))}
                            sx={{ p: 0.15 }}
                          />
                        </TableCell>
                      )
                    }
                    if (col.kind === 'editKg') {
                      return (
                        <TableCell
                          key={col.key}
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{ py: 0.1, px: 0.3 }}
                        >
                          {/* Lazy mount: chỉ render TextField cho row đã tick →
                              tránh mount nhiều input khi mở dialog rows lớn. */}
                          {isChecked ? (
                            <KgEditCell
                              cn={cn}
                              pendingValue={row[PENDING_KEY]}
                              externalValue={matchKg[cn] ?? ''}
                              width={col.width}
                              onCommit={onMatchKgChange}
                            />
                          ) : null}
                        </TableCell>
                      )
                    }
                    const text = fmtCell(row, col)
                    // CF cột Pending: p_plan_pending < 0 → đỏ + bold (acLessThan, value 0).
                    const cfRed = col.key === 'pPlanPending' && toNumber(row.pPlanPending) < 0
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          py: 0.15,
                          px: 0.4,
                          fontSize: '0.62rem',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(cfRed && { color: '#ED1C24', fontWeight: 700 }),
                        }}
                      >
                        <Box
                          component="span"
                          title={text || '-'}
                          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {text || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )
            }}
            components={{
              Table: (props: any) => (
                <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />
              ),
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number(props['data-item-index'])
                const row = Number.isFinite(idx) ? sortedRows[idx] : null
                const cn = row ? String((row as Record<string, unknown>).contractNo ?? '') : ''
                const isChecked = checked.has(cn)
                return (
                  <TableRow
                    {...props}
                    hover
                    sx={{
                      ...(isChecked && {
                        '& .MuiTableCell-root': {
                          bgcolor: `${alpha(accent, 0.14)} !important`,
                        },
                      }),
                    }}
                  />
                )
              },
            }}
          />
        )}
      </Box>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder="Contract no, supplier, place..."
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />
    </Paper>
  )
}

/* ---------- Factory/Stock quality panel (read-only) ----------------------- */

interface FactoryStockPanelProps {
  category: string
  onCategoryChange: (v: string) => void
  factoryCode: string
  onFactoryChange: (v: string) => void
  factoryOptions: StockPFactoryOption[]
  categoryOptions: string[]
  rows: Record<string, unknown>[]
  isFetching: boolean
}

function FactoryStockPanel({
  category,
  onCategoryChange,
  factoryCode,
  onFactoryChange,
  factoryOptions,
  categoryOptions,
  rows,
  isFetching,
}: FactoryStockPanelProps) {
  const columns = STOCK_COLS
  const accent = ACCENT_STOCK

  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns)
          && rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columnFilters.filters, columns],
  )
  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        columns as ReadonlyArray<TableSortColumn<string>>,
      ),
    [filteredRows, sortState, columns],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const toggleSort = (key: string) => setSortState((prev) => toggleTableSort(prev, key))

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderLeftWidth: 3,
        borderLeftColor: accent,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ px: 0.5, py: 0.3, bgcolor: alpha(accent, 0.06), borderBottom: 1, borderColor: 'divider' }}
      >
        <Chip
          size="small"
          label="TLC"
          sx={{ height: 18, bgcolor: accent, color: '#fff', fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem' } }}
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', color: 'text.secondary' }}
        >
          Factory Stock ({sortedRows.length.toLocaleString('en-US')}
          {sortedRows.length !== rows.length ? `/${rows.length.toLocaleString('en-US')}` : ''})
        </Typography>
        {isFetching ? <CircularProgress size={10} sx={{ ml: 0.5, color: accent }} /> : null}
        <Box sx={{ width: 8 }} />
        <Autocomplete
          size="small"
          options={categoryOptions}
          value={category || null}
          onChange={(_e, v) => onCategoryChange(v ?? '')}
          getOptionLabel={(o) => o || ''}
          isOptionEqualToValue={(a, b) => a === b}
          sx={{ flex: '1 1 0', minWidth: { xs: 72, sm: 90 } }}
          renderInput={(params) => (
            <TextField {...params} label="Grade"
              placeholder="(all)"
              InputLabelProps={{ shrink: true }}
              sx={matchingFilterAutocompleteSx} />
          )}
        />
        <Autocomplete
          size="small"
          options={factoryOptions}
          value={factoryOptions.find((f) => f.factory_code === factoryCode)
            ?? (factoryCode ? ({ factory_code: factoryCode, receive_warehouse: '' } as typeof factoryOptions[number]) : null)}
          onChange={(_e, v) => onFactoryChange(v?.factory_code ?? '')}
          getOptionLabel={(o) => o ? `${o.factory_code}${o.receive_warehouse ? ` — ${o.receive_warehouse}` : ''}` : ''}
          isOptionEqualToValue={(a, b) => a.factory_code === b.factory_code}
          sx={{ flex: '1.5 1 0', minWidth: { xs: 110, sm: 150 } }}
          renderInput={(params) => (
            <TextField {...params} label="Factory"
              placeholder="All"
              InputLabelProps={{ shrink: true }}
              sx={matchingFilterAutocompleteSx} />
          )}
        />
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
        {hasActiveFilters ? (
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={clearAllFilters}
            sx={filterBarBtnSx}
          >
            Clear ({activeFilterCount})
          </Button>
        ) : null}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
        {sortedRows.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
            }}
          >
            <Typography variant="caption" color="text.disabled">
              {!factoryCode
                ? 'Choose a factory to view stock.'
                : rows.length === 0
                  ? 'No stock/quality rows for this factory.'
                  : 'No rows match the current filters.'}
            </Typography>
          </Box>
        ) : (
          <TableVirtuoso
            style={{ height: '100%' }}
            data={sortedRows}
            fixedHeaderContent={() => (
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    onClick={() => toggleSort(col.key)}
                    style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                    sx={{ ...HEADER_CELL_SX, cursor: 'pointer', userSelect: 'none' }}
                  >
                    {col.label}
                    {getTableSortSuffix(sortState, col.key)}
                    {col.filter !== 'none' ? (
                      <ColumnFilterIcon
                        active={columnFilters.isActive(col.key)}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_, row) => (
              <>
                {columns.map((col) => {
                  const text = fmtCell(row, col)
                  // Access CF (frmMatchingPSQryReportStockQuality): stock_available < 0
                  // → chữ đỏ #ED1C24 (acLessThan, value 0). Chỉ cột này có CF.
                  const cfRed = col.key === 'stockAvailable' && toNumber(row.stockAvailable) < 0
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{
                        py: 0.15,
                        px: 0.4,
                        fontSize: '0.62rem',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        ...(cfRed && { color: '#ED1C24', fontWeight: 700 }),
                      }}
                    >
                      <Box
                        component="span"
                        title={text || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {text || '-'}
                      </Box>
                    </TableCell>
                  )
                })}
              </>
            )}
            components={{
              Table: (props: any) => (
                <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />
              ),
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => <TableRow {...props} hover />,
            }}
          />
        )}
      </Box>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder="Quality, grade, description..."
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />
    </Paper>
  )
}
